import React from 'react'
import { cn } from '../lib/utils'
import { CONDITION_STYLE, LABELS } from '../lib/conditionColors'

// FDI two-digit notation. Upper arch: Q1 (18→11) + Q2 (21→28).
// Lower arch: Q4 (48→41) + Q3 (31→38).
export const UPPER = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28]
export const LOWER = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38]

// The inclusive run of teeth between two FDI numbers ALONG THE SAME ARCH (a bridge/arch span),
// in arch order — e.g. toothSpan(13, 23) => ["13","12","11","21","22","23"] across the midline.
// Returns null when the two teeth are on different arches (no sensible span). (Paul 2026-08-23.)
export function toothSpan(a, b) {
  const A = Number(a), B = Number(b)
  for (const arch of [UPPER, LOWER]) {
    const i = arch.indexOf(A), j = arch.indexOf(B)
    if (i !== -1 && j !== -1) {
      const [lo, hi] = i <= j ? [i, j] : [j, i]
      return arch.slice(lo, hi + 1).map(String)
    }
  }
  return null
}

// Plan-status overlay ring: at a glance a tooth is existing (condition only, no ring),
// PLANNED (RED ring) or DONE (BLACK ring). Completed wins over planned when a tooth has both.
//
// 🚨 RED = OUTSTANDING, BLACK = DONE, and never the other way round (Paul 2026-09-12). That is
// the convention on the paper chart this replaces, and the one already written into
// lib/conditionColors.js for the condition layer. This ring used blue/green until 2026-09-12,
// which meant the screen disagreed with the paper AND with the rest of the app — a dentist
// should never have to translate a colour at a glance.
//
// The ring is deliberately a STRONG red/black against the pastel condition fills beneath it
// (caries is bg-red-100), so the ring reads as the ring and not as part of the fill.
const STATUS_RING = {
  planned:   'ring-2 ring-red-600 ring-offset-1',
  completed: 'ring-2 ring-gray-900 ring-offset-1',
}
const STATUS_LABEL = { planned: 'Planned', completed: 'Done' }

function Tooth({ number, condition, onClick, detail, status, isAnchor }) {
  const style = CONDITION_STYLE[condition] || CONDITION_STYLE.healthy
  const statusTitle = status ? ` · ${STATUS_LABEL[status] || status}` : ''
  return (
    <button
      type="button"
      onClick={onClick ? () => onClick(number) : undefined}
      title={`Tooth ${number}${condition ? ` — ${LABELS[condition] || condition}` : ''}${statusTitle}${detail ? ` · ${detail}` : ''}`}
      className={cn(
        'flex h-10 w-9 flex-col items-center justify-center rounded-md border text-[11px] font-semibold transition-transform',
        style,
        status && STATUS_RING[status],
        // range anchor waiting for its second tap — pulse so it's obvious which end is set
        isAnchor && 'ring-2 ring-brand-primary ring-offset-1 animate-pulse',
        onClick && 'hover:scale-105 cursor-pointer'
      )}
    >
      {number}
    </button>
  )
}

export default function Odontogram({ chart = {}, onToothClick = null, details = {}, toothStatus = {}, anchorTooth = null }) {
  const present = Object.entries(chart)
  const statuses = new Set(Object.values(toothStatus))
  const anchor = anchorTooth != null ? String(anchorTooth) : null
  return (
    <div className="rounded-xl border border-brand-border bg-white p-2 sm:p-4">
      {/* On a phone the 16-tooth arches are wider than the screen — scroll them horizontally
          rather than squashing the teeth; on tablet/laptop they centre as before. */}
      <div className="overflow-x-auto">
        <div className="mx-auto flex w-max flex-col items-center gap-1.5">
          <div className="flex gap-1">
            {UPPER.map((n) => <Tooth key={n} number={n} condition={chart[String(n)]} detail={details[String(n)]} status={toothStatus[String(n)]} isAnchor={anchor === String(n)} onClick={onToothClick} />)}
          </div>
          <div className="my-1 h-px w-full bg-brand-border" />
          <div className="flex gap-1">
            {LOWER.map((n) => <Tooth key={n} number={n} condition={chart[String(n)]} detail={details[String(n)]} status={toothStatus[String(n)]} isAnchor={anchor === String(n)} onClick={onToothClick} />)}
          </div>
        </div>
      </div>

      {/* Legend — plan-status rings first (what's planned vs done at a glance), then the conditions present */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {statuses.has('planned') && (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-white px-2 py-0.5 text-[11px] font-medium text-red-800">
            <span className="inline-block h-3 w-3 rounded ring-2 ring-red-600" /> Planned
          </span>
        )}
        {statuses.has('completed') && (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-900">
            <span className="inline-block h-3 w-3 rounded ring-2 ring-gray-900" /> Done
          </span>
        )}
        {statuses.size > 0 && present.length > 0 && <span className="mx-0.5 h-3 w-px bg-brand-border" />}
        {present.length === 0 && (
          <span className="text-xs text-brand-muted">No charted conditions yet — every tooth is shown as healthy.</span>
        )}
        {[...new Set(present.map(([, c]) => c))].map((c) => (
          <span key={c} className={cn('inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] font-medium', CONDITION_STYLE[c] || CONDITION_STYLE.healthy)}>
            {LABELS[c] || c}
          </span>
        ))}
      </div>
    </div>
  )
}
